<template>
  <div class="speedcheck">
    <LoadingPlaceholder v-if="preparing == true" />

    <SessionOverview
      v-show="preparing == false"
      v-if="runs != null && filter.startDate != null"
      :display-properties="['speed', 'createDate', 'card']"
      :runs="runs"
      :loading="loading"
      :filter="filter"
      :installation-type="installationType"
      :is-china-installation="isChinaInstallation"
      :installation-id="installationId"
      @loadSessions="loadSessions"
    />

    <SweetModal
      ref="speedcheckModal"
      title="Session"
      class="overflowHidden"
      @close="closeModal"
    >
      <template slot="box-action">
        <toggle-button
          v-if="!isChinaInstallation"
          v-model="viewDecoratedPhotos"
          :labels="{ checked: 'decorated', unchecked: 'normal' }"
          :width="100"
          :height="30"
          :font-size="12"
          class="mr-3 mt-2"
        />
      </template>
      <sweet-modal-tab
        id="tab1"
        title="Photo"
      >
        <LoadingPlaceholder v-if="itemLoading" />
        <template v-else>
          <img
            v-if="altImageAvailable"
            id="img"
            :src="altImageUrl"
            class="img-fluid"
          />
          <ImagePlaceholderSvg
            v-else
            class="img-fluid"
          />
        </template>
      </sweet-modal-tab>

      <sweet-modal-tab
        id="tab2"
        title="Details"
      >
        <table
          v-if="speedcheck != null"
          class="defaultTable"
        >
          <colgroup>
            <col width="200" />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <th>Id</th>
              <td class="lineBreaks">
                {{ speedcheck.id }}
              </td>
            </tr>
            <tr>
              <th>Card Number</th>
              <td class="lineBreaks">
                {{ speedcheck.cardId ? speedcheck.cardId.id : "" }}
              </td>
            </tr>
            <tr>
              <th>Create date</th>
              <td v-tooltip="dateTime_fromNow(speedcheck.startTime)">
                {{ dateTime_dateTime(speedcheck.startTime) }}
              </td>
            </tr>
            <tr>
              <th>Image Url</th>
              <td>
                {{
                  speedcheck.media && speedcheck.media.hd
                    ? speedcheck.media.hd.url
                    : ""
                }}
              </td>
            </tr>
            <tr>
              <th>Snapshot Url</th>
              <td>
                {{
                  speedcheck.media && speedcheck.media.preview
                    ? speedcheck.media.preview.url
                    : ""
                }}
              </td>
            </tr>
            <tr>
              <th>Speed</th>
              <td>{{ speedcheck.media ? speedcheck.media.speed : "" }}</td>
            </tr>
            <tr>
              <th>External Ids</th>
              <td class="lineBreaks">
                <template v-if="speedcheck.externalIds && speedcheck.externalIds.length > 0">
                  <div
                    v-for="(externalId, index) in speedcheck.externalIds"
                    :key="`speedcheck-externalId-${ index }`"
                  >
                    {{ externalId.type }}: {{ externalId.number }}
                  </div>
                </template>
                <template v-else>
                  {{ $t("noDataAvailable") }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </sweet-modal-tab>
      <sweet-modal-tab
        id="tab3"
        title="EXIF"
      >
        <pre v-if="exifData">{{ exifData }}</pre>
        <template v-else>
          {{ $t("noDataAvailable") }}
        </template>
      </sweet-modal-tab>
    </SweetModal>
  </div>
</template>

<script>
import { dateTimeMixin } from "@/mixins/dateTimeMixin.js";
import { SweetModal, SweetModalTab } from "sweet-modal-vue";

export default {
  name: "Speedcheck",
  components: {
    ImagePlaceholderSvg: () =>
      import("@/components/Base/ImagePlaceholderSvg.vue"),
    SessionOverview: () => import("@/components/Media/SessionOverview.vue"),
    SweetModal,
    SweetModalTab,
  },
  mixins: [dateTimeMixin],
  props: {
    installationId: {
      type: String,
      required: true,
    },
    installationType: {
      type: String,
      required: true,
    },
    installationCountry: {
      type: String,
      required: false,
      default: "",
    },
  },
  data() {
    return {
      useAlternativeFilter: true,
      loading: true,
      runs: null,
      altImageUrl: "",
      modalId: "speedcheckModal",
      filter: {
        startDate: null,
        endDate: null,
        cardNumber: null,
        showIncomplete: true,
        limit: 100,
      },
      preparing: true,
      payload: null,
      speedcheck: null,
      viewDecoratedPhotos: true,
      exifData: null,
      itemLoading: false,
    };
  },
  computed: {
    isChinaInstallation() {
      return this.installationCountry == "China";
    },
    altImageAvailable: function () {
      if (
        !this.itemLoading && ((this.getDecoratedImageUrl(this.payload)) ||
        (this.speedcheck &&
          this.speedcheck.media &&
          this.speedcheck.media.hd &&
          this.speedcheck.media.hd.url))
      ) {
        return true;
      }
      return false;
    },
  },
  watch: {
    viewDecoratedPhotos(val) {
      if (this.altImageAvailable) {
        this.altImageUrl = val
          ? this.getDecoratedImageUrl(this.payload) || this.getStandardImageUrl(this.speedcheck)
          : this.speedcheck &&
            this.speedcheck.media &&
            this.speedcheck.media.hd &&
            this.speedcheck.media.hd.url
          ? this.speedcheck.media.hd.url
          : this.getDecoratedImageUrl(this.payload);
      }
    },
  },
  created() {
    let tmpDate = new Date(Date.now());
    this.filter.startDate = new Date(
      tmpDate.getFullYear(),
      tmpDate.getMonth(),
      tmpDate.getDate() - 1,
      0,
      0,
      0
    );
    this.filter.endDate = new Date(
      tmpDate.getFullYear(),
      tmpDate.getMonth(),
      tmpDate.getDate(),
      23,
      59,
      59
    );
    this.filter.dates = [];
    this.filter.dates.push(this.filter.startDate, this.filter.endDate);

    this.loadSessions();
    this.$eventBus.$on(`openModal${this.modalId}`, this.openModal);
  },
  beforeDestroy() {
    this.$eventBus.$off(`openModal${this.modalId}`, this.openModal);
  },
  methods: {
    getDecoratedImageUrl(payload) {
      if (!payload) {
        return null;
      }

      return payload.decoratedUrl || payload.brandedImageUrl || null;
    },
    getStandardImageUrl(payload) {
      if (!payload) {
        return null;
      }

      if (payload.media && payload.media.hd && payload.media.hd.url) {
        return payload.media.hd.url;
      }

      return payload.url || payload.imageUrl || null;
    },
    loadSessions(payload) {
      if (payload == null || this.useAlternativeFilter == true) {
        payload = {
          limit: 100,
          dates: [
            this.useAlternativeFilter == true ? null : this.filter.startDate,
            this.useAlternativeFilter == true ? null : this.filter.endDate,
          ],
        };
      }
      this.loading = true;
      const reqObj = {
        installationId: this.installationId,
        startDate: this.dateTime_isoString(payload.dates[0]),
        endDate: this.dateTime_isoString(payload.dates[1]),
        limit: payload.limit
      }
      this.axios
        .post(
          `/Media/GetSpeedcheckMedia`, reqObj
        )
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          this.runs = response.data;
        })
        .finally(() => {
          this.loading = false;
          window.setTimeout(() => {
            this.preparing = false;
          }, 500);
        });
    },
    getExifData(payload) {
      if (payload.media && payload.media.hd && payload.media.hd.url) {
        var urlObj = {
          url: payload.media.hd.url,
        };

        this.axios.post(`/Media/GetExifData`, urlObj).then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          if (response.data) {
            this.exifData = JSON.stringify(JSON.parse(response.data), null, 2);
          }
        });
      }
    },
    getItem(id) {
      this.itemLoading = true;
      this.axios
        .get(`/Media/GetItem/${id}`)
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          this.speedcheck = response.data;
          this.getExifData(this.speedcheck);
          if (this.viewDecoratedPhotos) {
            this.altImageUrl = this.getDecoratedImageUrl(this.payload) || this.getStandardImageUrl(this.speedcheck);
          } else {
            this.altImageUrl = this.getStandardImageUrl(this.speedcheck);
          }
        })
        .finally(() => {
          this.itemLoading = false;
        });
    },
    // toggle the modal-element (the lightbox)
    openModal(payload) {
      this.payload = payload;
      if (this.isChinaInstallation) {
        this.viewDecoratedPhotos = false;
      }
      this.getItem(payload.id);
      this.$refs.speedcheckModal.open();
    },
    closeModal() {
      this.payload = null;
      this.speedcheck = null;
      this.altImageUrl = "";
      this.exifData = null;
    },
  },
};
</script>

<style scoped>
input[disabled] {
  cursor: default;
}
.modal-body video,
.modal-body .row .col {
  margin-bottom: 1rem;
}
</style>
